import React, { useId } from "react";
import PropTypes from "prop-types";
import { ArrowRight } from "lucide-react";

import TextInput from "./TextInput";
import ValidationMessage from "./ValidationMessage";

const toComparable = (value, type) => {
  if (!value) return null;
  if (type === "time") {
    const [hours, minutes] = value.split(":").map(Number);
    return (hours * 60) + minutes;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp / 60000;
};

export const getDateTimeRangeError = ({ end, minimumDurationMinutes = 0, start, type = "datetime-local" }) => {
  const startValue = toComparable(start, type);
  const endValue = toComparable(end, type);
  if (startValue === null || endValue === null) return "";
  if (endValue < startValue) return "The end must be after the start.";
  if (minimumDurationMinutes && endValue - startValue < minimumDurationMinutes) {
    return `The range must be at least ${minimumDurationMinutes} minutes.`;
  }
  return "";
};

const DateTimeRange = ({
  disabled = false,
  end,
  endLabel = "End",
  error,
  helpText,
  id,
  label,
  max,
  min,
  minimumDurationMinutes = 0,
  onChange,
  readOnly = false,
  required = false,
  start,
  startLabel = "Start",
  step,
  type = "datetime-local",
}) => {
  const generatedId = useId();
  const groupId = id || generatedId;
  const helpId = helpText ? `${groupId}-help` : undefined;
  const calculatedError = getDateTimeRangeError({ end, minimumDurationMinutes, start, type });
  const rawError = error || calculatedError;
  const currentError = typeof rawError === "string"
    ? rawError
    : rawError?.message || (rawError ? "Enter a valid start and end date." : "");
  const errorId = currentError ? `${groupId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <fieldset className={["wsm-date-range", currentError && "wsm-date-range--error"].filter(Boolean).join(" ")} aria-describedby={describedBy}>
      <legend>{label}{required && <span aria-hidden="true"> *</span>}</legend>
      <div className="wsm-date-range__controls">
        <label htmlFor={`${groupId}-start`}><span>{startLabel}</span><TextInput aria-invalid={Boolean(currentError)} disabled={disabled} id={`${groupId}-start`} max={max} min={min} onChange={(event) => onChange({ start: event.target.value, end })} readOnly={readOnly} required={required} step={step} type={type} value={start} /></label>
        <ArrowRight aria-hidden="true" className="wsm-date-range__separator" />
        <label htmlFor={`${groupId}-end`}><span>{endLabel}</span><TextInput aria-invalid={Boolean(currentError)} disabled={disabled} id={`${groupId}-end`} max={max} min={min} onChange={(event) => onChange({ start, end: event.target.value })} readOnly={readOnly} required={required} step={step} type={type} value={end} /></label>
      </div>
      {helpText && <p className="wsm-date-range__help" id={helpId}>{helpText}</p>}
      {currentError && <ValidationMessage className="wsm-date-range__error" id={errorId} message={currentError} />}
    </fieldset>
  );
};

DateTimeRange.propTypes = {
  disabled: PropTypes.bool,
  end: PropTypes.string.isRequired,
  endLabel: PropTypes.string,
  error: PropTypes.string,
  helpText: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  max: PropTypes.string,
  min: PropTypes.string,
  minimumDurationMinutes: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  start: PropTypes.string.isRequired,
  startLabel: PropTypes.string,
  step: PropTypes.number,
  type: PropTypes.oneOf(["date", "datetime-local", "time"]),
};

export default DateTimeRange;
